// Which orders Trail reads from the ERP (owner 2026-11-02, after a hidden 365-day floor cancelled
// every purchase order of the demo company): the rule and the date floor per order kind as real
// fields on top of the adapter's `rules` block, with the ERP column named, the value's origin shown,
// and a "Just nu" check against the mirror before anything is saved. Mockup admin-v1/erp-order-scope.

import { useEffect, useState } from "react";
import { Admin, type ScopeCheck } from "../api/client.js";
import { t } from "../i18n.js";
import { Seg } from "./settings-ui.jsx";

type Floor = { mode: "days"; days: number } | { mode: "date"; date: string } | { mode: "none" };
type Rule = { kind?: string; floor?: Floor; since?: string; sinceDays?: number; flag?: string };
type RulesBlock = { salesOrders?: Rule; purchaseOrders?: Rule; [k: string]: unknown };

const KINDS = { salesOrders: ["confirmed-today", "confirmed-by-today", "flag"], purchaseOrders: ["open", "flag"] } as const;

/** The floor as the adapter resolves it: explicit `floor`, else the older `since` / `sinceDays`, else none. */
function floorOf(r: Rule | undefined): Floor {
  if (r?.floor) return r.floor;
  if (r?.since) return { mode: "date", date: r.since };
  if (r?.sinceDays && r.sinceDays > 0) return { mode: "days", days: r.sinceDays };
  return { mode: "none" };
}
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
const fromDate = (f: Floor): string | null => (f.mode === "date" ? f.date || null : f.mode === "days" ? daysAgo(f.days) : null);
/** Which ERP date the floor compares against: the confirmed rules use the confirmed delivery date, the rest the order date. */
const columnOf = (kind: "salesOrders" | "purchaseOrders", rule: string | undefined): "orderDate" | "deliveryDate" => (kind === "salesOrders" && (rule === "confirmed-by-today" || rule === "confirmed-today") ? "deliveryDate" : "orderDate");

export function OrderScope({ defaults, draft, onChange }: { defaults: RulesBlock; draft: RulesBlock | null; onChange: (next: RulesBlock) => void }) {
  const effective: RulesBlock = { ...defaults, ...(draft ?? {}) };
  const set = (kind: "salesOrders" | "purchaseOrders", patch: Partial<Rule>) => {
    const cur = { ...(effective[kind] ?? {}) } as Rule;
    const next = { ...cur, ...patch } as Rule;
    if (patch.floor) { delete next.since; delete next.sinceDays; }
    onChange({ ...effective, [kind]: next });
  };
  return (
    <div className="card" style={{ padding: 0, marginTop: 10 }}>
      <div className="hd" style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", fontWeight: 700 }}>{t("adm.scope.title")} <span className="ahint" style={{ fontWeight: 500 }}>{t("adm.scope.sub")}</span></div>
      {(["salesOrders", "purchaseOrders"] as const).map((kind) => {
        const r = (effective[kind] ?? {}) as Rule;
        const own = draft?.[kind] !== undefined && (draft[kind] as Rule)?.floor !== undefined;
        const floor = floorOf(r);
        return <ScopeKind key={kind} kind={kind} rule={r} floor={floor} own={own} onKind={(k) => set(kind, { kind: k })} onFloor={(f) => set(kind, { floor: f })} />;
      })}
    </div>
  );
}

function ScopeKind({ kind, rule, floor, own, onKind, onFloor }: { kind: "salesOrders" | "purchaseOrders"; rule: Rule; floor: Floor; own: boolean; onKind: (k: string) => void; onFloor: (f: Floor) => void }) {
  const [check, setCheck] = useState<ScopeCheck | null>(null);
  const column = columnOf(kind, rule.kind);
  const from = fromDate(floor);
  useEffect(() => {
    let live = true;
    Admin.scopeCheck({ orderKind: kind === "salesOrders" ? "sales-order" : "expected-receipt", column, from }).then((c) => { if (live) setCheck(c); }).catch(() => { if (live) setCheck(null); });
    return () => { live = false; };
  }, [kind, column, from]);
  const kinds = KINDS[kind].map((k) => ({ v: k, l: t(`adm.scope.kind.${k}`) }));
  const colName = column === "deliveryDate" ? t("adm.scope.col.deliveryDate") : t("adm.scope.col.orderDate");
  const bad = check && from && check.outside > 0;
  const noFloorRule = rule.kind === "confirmed-today";
  return (
    <div style={{ borderBottom: "1px solid var(--line)" }}>
      <div style={{ padding: "10px 14px 0", fontWeight: 700, fontSize: 15 }}>{t(`adm.scope.${kind}`)}</div>
      <div className="aform" style={{ padding: "6px 14px 0" }}>
        <span>{t("adm.scope.rule")}</span>
        <div>
          <Seg value={(rule.kind ?? kinds[0]!.v) as string} options={kinds} onChange={onKind} />
          <div className="hint">{t(`adm.scope.ruleHint.${kind}`)}</div>
        </div>
        <span>{t("adm.scope.floor")}</span>
        {rule.kind === "confirmed-today" ? (
          <div className="hint" style={{ paddingTop: 6 }}>{t("adm.scope.floor.notForToday")}</div>
        ) : (
        <div>
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Seg value={floor.mode} options={[{ v: "days", l: t("adm.scope.floor.days") }, { v: "date", l: t("adm.scope.floor.date") }, { v: "none", l: t("adm.scope.floor.none") }]} onChange={(m) => onFloor(m === "days" ? { mode: "days", days: floor.mode === "days" ? floor.days : 365 } : m === "date" ? { mode: "date", date: floor.mode === "date" ? floor.date : daysAgo(365) } : { mode: "none" })} />
            {floor.mode === "days" && <><input className="ain" style={{ width: 70 }} inputMode="numeric" value={floor.days} onChange={(e) => onFloor({ mode: "days", days: Math.max(1, Number(e.target.value.replace(/[^0-9]/g, "")) || 1) })} /> <span>{t("adm.scope.daysBack")}</span></>}
            {floor.mode === "date" && <input className="ain" type="date" style={{ width: 150 }} value={floor.date} onChange={(e) => onFloor({ mode: "date", date: e.target.value })} />}
            {own ? <span className="pill ok">{t("adm.scope.own")}</span> : <span className="pill mut">{t("adm.scope.default")}</span>}
          </span>
          <div className="hint">{t("adm.scope.floorHint", { col: colName })}</div>
        </div>
        )}
      </div>
      {!noFloorRule && <div style={{ margin: "10px 14px 12px", padding: "8px 12px", borderRadius: 10, background: bad ? "var(--bad-bg)" : "var(--panel-2)", border: bad ? "1px solid var(--bad)" : "1px solid transparent", fontSize: 13 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: bad ? "var(--bad)" : "var(--muted)" }}>{bad ? t("adm.scope.now.bad", { n: check!.outside }) : t("adm.scope.now")}</div>
        {!check && <div className="mut">{t("adm.scope.now.loading")}</div>}
        {check && !from && <div>{t("adm.scope.now.noFloor", { open: check.open })}</div>}
        {check && from && !bad && <div>{t("adm.scope.now.ok", { from, open: check.open })}</div>}
        {check && from && bad && <div>{t("adm.scope.now.badText", { from, min: check.min ?? "—", max: check.max ?? "—", col: colName })}</div>}
        {check && check.missing > 0 && column === "orderDate" && <div className="mut">{t("adm.scope.now.missing", { n: check.missing })}</div>}
      </div>}
    </div>
  );
}

export const scopeFromRules = floorOf;
